import { ArenaGameState } from '../../state/ArenaTypes';
import { GameTableConfig } from '../../engine/tableConfig/types';
import { ScoreDisplayResult, BooksToSetResult, HandScoreResult } from '../common/IGameRules';
import { IScorePresenter } from '../common/IScorePresenter';

/**
 * Presentation-only score formatter for Bid Whist (Contract Deficit scoring).
 *
 * The server is authoritative for all scoring, contracts, bids,
 * trick counts, deficit targets, outcomes, and match state.
 *
 * This class only formats values already present in authoritative state.
 */
export class BidWhistScorePresenter implements IScorePresenter {
  public formatScoreDisplay(gameState: ArenaGameState, config: GameTableConfig): ScoreDisplayResult {
    return {
      team1Label: String(gameState.team1Score ?? 0),
      team2Label: String(gameState.team2Score ?? 0),
      team1Details: `${gameState.team1Score ?? 0} pts`,
      team2Details: `${gameState.team2Score ?? 0} pts`,
      targetScoreLabel: `Target: ${config.scoring.targetScore}`
    };
  }

  public calculateBooksToSet(gameState: ArenaGameState): BooksToSetResult {
    const winningBid = gameState.winningBid ?? (gameState.gameExt as any)?.winningBid ?? null;
    const winningBidderSeat = (gameState.winningBidderSeat !== undefined && gameState.winningBidderSeat !== null)
      ? Number(gameState.winningBidderSeat)
      : ((gameState.gameExt as any)?.winningBidderSeat !== undefined && (gameState.gameExt as any)?.winningBidderSeat !== null)
        ? Number((gameState.gameExt as any).winningBidderSeat)
        : null;

    if (
      gameState.phase === 'LOBBY' ||
      gameState.phase === 'DEALING' ||
      gameState.phase === 'BIDDING' ||
      winningBid === null ||
      winningBidderSeat === null
    ) {
      return {
        relevantTeam: null,
        booksNeededToSet: 0,
        booksToSetTarget: 0,
        defendingBooksWon: 0,
        declarerTeam: null,
        teams: {}
      };
    }

    const declarerTeam = (winningBidderSeat === 0 || winningBidderSeat === 2) ? 1 : 2;
    const defendingBooksWon = declarerTeam === 1
      ? (gameState.team2Books ?? 0)
      : (gameState.team1Books ?? 0);
    const declarerBooksWon = declarerTeam === 1
      ? (gameState.team1Books ?? 0)
      : (gameState.team2Books ?? 0);

    const booksToSetTarget = 8 - winningBid;
    const booksNeededToSet = Math.max(0, booksToSetTarget - defendingBooksWon);
    const declarerTarget = winningBid + 6;
    const declarerNeeded = Math.max(0, declarerTarget - declarerBooksWon);

    // If server provided booksToSet, ensure it matches the current contract's target before trusting
    if (gameState.booksToSet) {
      const serverBts = gameState.booksToSet as BooksToSetResult;
      if (serverBts.booksToSetTarget === booksToSetTarget) {
        return serverBts;
      }
    }

      return {
        relevantTeam: declarerTeam,
        booksNeededToSet,
        booksToSetTarget,
        defendingBooksWon,
        declarerTeam,
        teams: {
          1: {
            team: 1,
            target: declarerTeam === 1 ? declarerTarget : booksToSetTarget,
            current: gameState.team1Books ?? 0,
            remaining: Math.max(0, (declarerTeam === 1 ? declarerTarget : booksToSetTarget) - (gameState.team1Books ?? 0)),
            needed: declarerTeam === 1 ? declarerNeeded : booksNeededToSet,
          },
          2: {
            team: 2,
            target: declarerTeam === 2 ? declarerTarget : booksToSetTarget,
            current: gameState.team2Books ?? 0,
            remaining: Math.max(0, (declarerTeam === 2 ? declarerTarget : booksToSetTarget) - (gameState.team2Books ?? 0)),
            needed: declarerTeam === 2 ? declarerNeeded : booksNeededToSet,
          }
        },
        team1: {
          team: 1,
          target: declarerTeam === 1 ? declarerTarget : booksToSetTarget,
          current: gameState.team1Books ?? 0,
          remaining: Math.max(0, (declarerTeam === 1 ? declarerTarget : booksToSetTarget) - (gameState.team1Books ?? 0)),
          needed: declarerTeam === 1 ? declarerNeeded : booksNeededToSet,
        },
        team2: {
          team: 2,
          target: declarerTeam === 2 ? declarerTarget : booksToSetTarget,
          current: gameState.team2Books ?? 0,
          remaining: Math.max(0, (declarerTeam === 2 ? declarerTarget : booksToSetTarget) - (gameState.team2Books ?? 0)),
          needed: declarerTeam === 2 ? declarerNeeded : booksNeededToSet,
        }
      };
  }

  public calculateHandScore(gameState: ArenaGameState, _config: GameTableConfig): HandScoreResult {
    const history = Array.isArray(gameState.scoreHistory) ? gameState.scoreHistory : [];
    const latest = history.find(h => h && h.handNumber === gameState.handNumber) || (history.length > 0 ? history[history.length - 1] : null);
    const isBoston = Boolean(gameState.isBoston ?? (gameState.gameExt as any)?.isBoston);
    const bostonTeam = gameState.bostonTeam ?? (gameState.gameExt as any)?.bostonTeam ?? 1;

    if (latest) {
      return {
        teamScores: {
          1: latest.team1Score ?? gameState.team1Score ?? 0,
          2: latest.team2Score ?? gameState.team2Score ?? 0
        },
        teamDeltas: {
          1: latest.pointsTeam1 ?? 0,
          2: latest.pointsTeam2 ?? 0
        },
        contracts: [
          {
            team: (latest.bidderTeam ?? 1) as 1 | 2,
            bidAmount: gameState.winningBid ?? (gameState.gameExt as any)?.winningBid ?? 0,
            contractLabel: latest.contract ?? 'Contract',
            booksWon: latest.bidderBooks ?? 0,
            neededBooks: latest.neededBooks ?? 0,
            isMade: latest.result === 'MADE' || latest.result === 'BOSTON',
            isSet: Boolean(latest.result === 'SET' || gameState.isSet),
            points: latest.pointsTeam1 ?? 0,
            result: latest.result ?? (gameState.isSet ? 'SET' : 'MADE')
          }
        ],
        summary: latest.summary || gameState.lastActionMessage || '',
        isSet: Boolean(gameState.isSet || latest.result === 'SET'),
        setTeam: gameState.setTeam ?? (latest.result === 'SET' ? latest.bidderTeam : null),
        celebration: isBoston ? { type: 'boston', isUserTeam: bostonTeam === 1, winningTeam: bostonTeam, title: 'Boston Sweep!' } : null
      };
    }

    return {
      teamScores: {
        1: gameState.team1Score ?? 0,
        2: gameState.team2Score ?? 0
      },
      teamDeltas: {
        1: 0,
        2: 0
      },
      contracts: [],
      summary: gameState.lastActionMessage || '',
      isSet: Boolean(gameState.isSet),
      setTeam: gameState.setTeam ?? null,
      celebration: isBoston ? { type: 'boston', isUserTeam: bostonTeam === 1, winningTeam: bostonTeam, title: 'Boston Sweep!' } : null
    };
  }
}
